import React, { useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Box,
  Typography,
  Badge,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  FlashOn as FlashOnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { ObservatoryEvent } from './hooks/useAIObservatory';

interface ActivityStreamPanelProps {
  events: ObservatoryEvent[];
  onClearEvents: () => void;
}

type GroupingMode = 'thread' | 'user' | 'none';

export const ActivityStreamPanel: React.FC<ActivityStreamPanelProps> = ({
  events,
  onClearEvents,
}) => {
  const [groupingMode, setGroupingMode] = useState<GroupingMode>('thread');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group events by thread or user
  const groupedEvents = useMemo(() => {
    if (groupingMode === 'none') {
      return { ungrouped: events };
    }

    const groups: Record<string, ObservatoryEvent[]> = {};
    
    events.forEach(event => {
      const groupKey = groupingMode === 'thread' 
        ? event.data.threadId || 'no-thread'
        : event.data.userId || 'unknown-user';
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(event);
    });

    return groups;
  }, [events, groupingMode]);

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'thread:created':
        return '🧵';
      case 'message:sent':
        return '💬';
      case 'function:called':
        return '⚙️';
      case 'session:started':
        return '▶️';
      case 'session:ended':
        return '⏹️';
      default:
        return '📊';
    }
  };

  const getEventColor = (eventType: string): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (eventType) {
      case 'thread:created':
        return 'primary';
      case 'message:sent':
        return 'secondary';
      case 'function:called':
        return 'info';
      case 'session:started':
        return 'success';
      case 'session:ended':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 1000) return 'just now';
    if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return date.toLocaleTimeString();
  };

  const getEventSummary = (event: ObservatoryEvent) => {
    const userId = event.data.userId;
    const userDisplay = userId ? ` (${userId.substring(0, 12)}...)` : '';
    
    switch (event.type) {
      case 'thread:created':
        return `New thread: ${event.data.title || event.data.threadId}${userDisplay}`;
      case 'message:sent':
        return `${event.data.role}: ${event.data.contentPreview?.substring(0, 50)}...`;
      case 'function:called':
        return `${event.data.functionName}() ${event.data.success ? '✅' : '❌'} (${event.data.executionTimeMs}ms)${userDisplay}`;
      case 'thread:updated':
        return `Thread updated${userDisplay}`;
      case 'session:started':
        return `Session started (${event.data.sessionType})${userDisplay}`;
      case 'session:ended':
        return `Session ended (${event.data.messageCount} messages, ${event.data.duration}ms)`;
      default:
        return JSON.stringify(event.data).substring(0, 100);
    }
  };

  const renderEventItem = (event: ObservatoryEvent, index: number) => (
    <ListItem key={`${event.timestamp}-${index}`} sx={{ pl: groupingMode !== 'none' ? 4 : 2 }}>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <span style={{ fontSize: '1.2em' }}>{getEventIcon(event.type)}</span>
            <Chip label={event.type} color={getEventColor(event.type)} size="small" />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 14 }} />
              {formatTimestamp(event.timestamp)}
            </Typography>
          </Box>
        }
        secondary={
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {getEventSummary(event)}
          </Typography>
        }
      />
    </ListItem>
  );

  const renderGroupHeader = (groupKey: string, groupEvents: ObservatoryEvent[]) => {
    const isExpanded = expandedGroups.has(groupKey);
    const firstEvent = groupEvents[0];
    const threadId = groupingMode === 'thread' ? groupKey : firstEvent?.data.threadId;
    const userId = groupingMode === 'user' ? groupKey : firstEvent?.data.userId;
    
    // Count different event types in this group
    const messageCount = groupEvents.filter(e => e.type === 'message:sent').length;
    const functionCount = groupEvents.filter(e => e.type === 'function:called').length;
    
    return (
      <ListItem
        button
        onClick={() => toggleGroup(groupKey)}
        sx={{
          bgcolor: 'background.default',
          borderLeft: 3,
          borderColor: 'primary.main',
          mb: 0.5,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {groupingMode === 'thread' ? <ForumIcon /> : <PersonIcon />}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {groupingMode === 'thread' 
                  ? `Thread ${groupKey.substring(7, 20)}...`
                  : `User ${groupKey.substring(0, 20)}...`
                }
              </Typography>
              <Badge badgeContent={groupEvents.length} color="primary" sx={{ ml: 1 }} />
              <Box sx={{ flex: 1 }} />
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {messageCount} message{messageCount !== 1 ? 's' : ''} • 
              {functionCount} function{functionCount !== 1 ? 's' : ''} • 
              Last: {formatTimestamp(groupEvents[0].timestamp)}
              {groupingMode === 'thread' && userId && (
                <> • User: {userId.substring(0, 12)}...</>
              )}
              {groupingMode === 'user' && threadId && (
                <> • Thread: {threadId.substring(7, 15)}...</>
              )}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  return (
    <Card>
      <CardHeader
        title={<><FlashOnIcon /> Activity Stream</>}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`${events.length} events`} color="primary" />
            <IconButton onClick={onClearEvents} size="small">
              <DeleteIcon />
            </IconButton>
          </Box>
        }
      />
      <CardContent>
        {/* Grouping Controls */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={groupingMode}
            exclusive
            onChange={(e, newMode) => newMode && setGroupingMode(newMode)}
            size="small"
          >
            <ToggleButton value="thread">
              <ForumIcon sx={{ mr: 0.5, fontSize: 16 }} />
              By Thread
            </ToggleButton>
            <ToggleButton value="user">
              <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} />
              By User
            </ToggleButton>
            <ToggleButton value="none">
              <FlashOnIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Flat
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">
              No events yet. Activity will appear here in real-time.
            </Typography>
          </Box>
        ) : groupingMode === 'none' ? (
          // Flat list view
          <List sx={{ maxHeight: 600, overflow: 'auto' }}>
            {events.map((event, index) => renderEventItem(event, index))}
          </List>
        ) : (
          // Grouped view
          <List sx={{ maxHeight: 600, overflow: 'auto' }}>
            {Object.entries(groupedEvents).map(([groupKey, groupEvents]) => (
              <React.Fragment key={groupKey}>
                {renderGroupHeader(groupKey, groupEvents)}
                <Collapse in={expandedGroups.has(groupKey)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {groupEvents.map((event, index) => renderEventItem(event, index))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};
